import useSWR, { mutate } from "swr";
import { api } from "@/lib/app";
import { PushPin, Trash, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const KIND_LABEL = {
  kpi: { label: "KPI", color: "var(--brand)", bg: "#E8EEEA" },
  chart: { label: "Graf", color: "#1E3627", bg: "#E8EEEA" },
  anomaly: { label: "Anomália", color: "var(--warning)", bg: "var(--warning-bg)" },
  slot: { label: "Slot efektivity", color: "var(--critical)", bg: "var(--critical-bg)" },
  day: { label: "Deň", color: "#3E7B52", bg: "var(--success-bg)" },
  weekday: { label: "Deň v týždni", color: "#3E7B52", bg: "var(--success-bg)" },
};

export default function Highlights({ storeId }) {
  const { data: items = [] } = useSWR(`/stores/${storeId}/highlights`, fetcher);

  const remove = async (id) => {
    if (!confirm("Odznačiť túto položku?")) return;
    await api.delete(`/highlights/${id}`);
    mutate(`/stores/${storeId}/highlights`);
    toast.success("Položka odznačená");
  };

  return (
    <div className="space-y-4">
      <div className="card-flat p-4 text-sm text-[var(--text-secondary)]">
        <div className="flex items-start gap-2">
          <PushPin size={18} weight="duotone" className="text-[var(--brand)] mt-0.5" />
          <p>
            Tu vidíte všetko, čo ste počas prezerania označili. Tieto položky <strong>AI zoberie do úvahy v záverečnej správe</strong> a&nbsp;
            objavia sa aj v <strong>Excel exporte</strong> (záložka „Označené body“) a v <strong>PDF exporte</strong> AI správy. Označiť môžete KPI v Prehľade, riadky Anomálií a Efektívnosti pomocou
            ikony ikonky „pripináčik“.
          </p>
        </div>
      </div>

      {items.length === 0 && (
        <div className="card-flat p-10 text-center text-[var(--text-secondary)]">
          <PushPin size={28} className="mx-auto mb-3 text-[var(--text-muted)]" />
          <p className="font-display font-semibold">Zatiaľ nič označené</p>
          <p className="text-sm mt-1">Kliknite na ikonu pripináčika pri ľubovoľnej KPI, anomálii alebo slote efektivity.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((h) => {
          const style = KIND_LABEL[h.kind] || KIND_LABEL.kpi;
          return (
            <div key={h.id} className="card-flat p-4" data-testid={`highlight-${h.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-medium"
                      style={{ background: style.bg, color: style.color }}
                    >
                      <PushPin size={10} weight="fill" /> {style.label}
                    </span>
                    {(h.period_from || h.period_to) && (
                      <span className="text-[10px] tabular text-[var(--text-muted)]">
                        {h.period_from || "—"} → {h.period_to || "—"}
                      </span>
                    )}
                    <span className="text-[10px] tabular text-[var(--text-muted)] ml-auto">
                      {new Date(h.created_at).toLocaleString("sk-SK")}
                    </span>
                  </div>
                  <p className="font-medium text-sm">{h.label}</p>
                  {h.comment && (
                    <div className="mt-2 pl-3 border-l-2 border-[var(--brand)] text-xs text-[var(--text-secondary)] flex items-start gap-1.5">
                      <ChatCircleDots size={12} className="text-[var(--brand)] mt-0.5 shrink-0" />
                      <p className="italic">{h.comment}</p>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => remove(h.id)}
                  className="text-[var(--text-muted)] hover:text-[var(--critical)] p-1"
                  data-testid={`highlight-remove-${h.id}`}
                  aria-label="Odznačiť"
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
